import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { useAuth } from '@spotify/confidence-core-react';

import type { OnboardingStartingPoint } from '../../config/onboardingSurvey';

type OnboardingGoalState = {
  goal: string;
  startingPoint?: OnboardingStartingPoint;
};

const OnboardingGoalContext = createContext<OnboardingGoalState>({ goal: '' });

type SetOnboardingGoal = (
  goal: string,
  startingPoint?: OnboardingStartingPoint,
) => void;

const SetOnboardingGoalContext = createContext<SetOnboardingGoal>(() => {});

export const OnboardingGoalProvider = ({
  children,
}: PropsWithChildren): JSX.Element => {
  const { orgName } = useAuth();
  const [pendingGoal, setPendingGoal] = useState<{
    orgName?: string;
    goal: string;
    startingPoint?: OnboardingStartingPoint;
  }>({ orgName, goal: '' });
  const setGoal = useCallback<SetOnboardingGoal>(
    (goal, startingPoint) => setPendingGoal({ orgName, goal, startingPoint }),
    [orgName],
  );
  const value = useMemo<OnboardingGoalState>(() => {
    const isCurrentOrg = pendingGoal.orgName === orgName;
    return {
      goal: isCurrentOrg ? pendingGoal.goal : '',
      startingPoint: isCurrentOrg ? pendingGoal.startingPoint : undefined,
    };
  }, [pendingGoal, orgName]);

  return (
    <SetOnboardingGoalContext.Provider value={setGoal}>
      <OnboardingGoalContext.Provider value={value}>
        {children}
      </OnboardingGoalContext.Provider>
    </SetOnboardingGoalContext.Provider>
  );
};

export const useOnboardingGoal = (): OnboardingGoalState =>
  useContext(OnboardingGoalContext);

export const useSetOnboardingGoal = (): SetOnboardingGoal =>
  useContext(SetOnboardingGoalContext);
